import React, { useMemo, useState } from "react"
import { Switch, useHistory, useParams } from "react-router-dom"
import { Box, Button, ButtonGroup, makeStyles, useMediaQuery } from "@material-ui/core"
import { Add as AddIcon } from "@material-ui/icons"
import { useTheme } from "@material-ui/styles"
import {
  AreaHeader,
  PrivateRoute,
  KnowledgeArticleCreator,
  FloatingActionButton,
  DesktopTrainingButton,
  RowBox,
  ScrollToTop,
} from "../../components"
import { useMutationDeleteCategory, useMutationDeleteKnowledge } from "../../data"
import Categories from "./Categories"
import Category from "./Category"
import Article from "./Article"
import { mapToIds, multipleSelectChange, toId, useKnowledgeUtils } from "../../utils"
import { KnowledgeFiltersButton } from "../../components/Knowledge/KnowledgeFiltersButton"
import { useAuth } from "../../services"

const useStyles = makeStyles(() => ({
  container: {
    position: "relative",
  },
  areaMobileFilters: {
    position: "absolute",
    right: 16,
    top: 6,
  },
}))

const Knowledge = () => {
  const classes = useStyles()
  const theme = useTheme()
  const xs = useMediaQuery(theme.breakpoints.down("xs"))
  const { id } = useParams()
  const { hasFeature } = useAuth()
  const history = useHistory()
  const [groups, setGroups] = useState(["all"])
  const [locations, setLocations] = useState(["all"])

  const [deleteKnowledge] = useMutationDeleteKnowledge()

  const [deleteCategory] = useMutationDeleteCategory()
  const { getNavigateToCategoryForID, getNavigateToArticleLinkForID } = useKnowledgeUtils()

  const groupIds = useMemo(() => {
    const filteredGroups = groups.filter((g) => g !== "all")
    return filteredGroups.length ? mapToIds(filteredGroups) : null
  }, [groups])

  const locationIds = useMemo(() => {
    const filteredLocations = locations.filter((l) => l !== "all")
    return filteredLocations.length ? mapToIds(filteredLocations) : null
  }, [locations])

  const handleGroupsChange = (event) => {
    setGroups([...multipleSelectChange(groups, event)])
  }
  const handleLocationsChange = (event) => {
    setLocations([...multipleSelectChange(locations, event)])
  }
  const handleRegionChange = (regionLocations) => {
    setLocations([...multipleSelectChange(locations, regionLocations)])
  }

  const handleCreateArticleClick = () => {
    history.push("/knowledge/article/new")
  }
  const handleCreateArticleClose = () => {
    history.push("/knowledge")
  }
  const handleCreateArticleCreated = (item) => {
    history.push(getNavigateToArticleLinkForID(item.id))
  }
  const handleEditArticle = (knowledge) => {
    history.push(`/knowledge/article/${toId(knowledge)}/edit`)
  }
  const handleDeleteArticle = async ({ id: deleteId, category }) => {
    const result = await deleteKnowledge({
      variables: {
        id: deleteId,
      },
    })

    if (result?.data?.deleteKnowledge) {
      history.push(getNavigateToCategoryForID(toId(category)))
    }
  }

  const handleEditCategory = (category) => {
    history.push(`/knowledge/category/${toId(category)}/edit`)
  }
  const handleDeleteCategory = async ({ id: deleteId }) => {
    await deleteCategory({
      variables: {
        id: deleteId,
      },
    })
  }

  const isNew = id === "new"

  const hasLocationsAndGroups = hasFeature("knowledge_access")

  return (
    <>
      <ScrollToTop />
      {isNew && (
        <KnowledgeArticleCreator
          open
          onClose={handleCreateArticleClose}
          onCreated={handleCreateArticleCreated}
          isInline={xs}
        />
      )}

      {(!xs || (xs && !isNew)) && (
        <Box className={classes.container}>
          <AreaHeader
            title="Knowledge Base"
            titleIcon="knowledge-light"
            desktopButtons={
              <RowBox>
                <Box mr={1}>
                  <DesktopTrainingButton />
                </Box>

                <Box mr={hasLocationsAndGroups ? 1 : 0}>
                  <ButtonGroup variant="contained" color="primary">
                    <Button onClick={handleCreateArticleClick} data-cy="Button-create">
                      Create article
                    </Button>
                  </ButtonGroup>
                </Box>

                {hasLocationsAndGroups && (
                  <KnowledgeFiltersButton
                    groups={groups}
                    locations={locations}
                    onGroupsChange={handleGroupsChange}
                    onLocationsChange={handleLocationsChange}
                    onRegionChange={handleRegionChange}
                    options={{
                      hasGroups: true,
                      hasLocations: true,
                    }}
                  />
                )}
              </RowBox>
            }
            mobileButtons={
              <>
                {hasLocationsAndGroups && (
                  <Box className={classes.areaMobileFilters}>
                    <KnowledgeFiltersButton
                      groups={groups}
                      locations={locations}
                      onGroupsChange={handleGroupsChange}
                      onLocationsChange={handleLocationsChange}
                      onRegionChange={handleRegionChange}
                      options={{
                        hasGroups: true,
                        hasLocations: true,
                      }}
                    />
                  </Box>
                )}

                <FloatingActionButton
                  color="primary"
                  aria-label="Create article"
                  onClick={handleCreateArticleClick}
                  data-cy="FloatingActionButton-create"
                >
                  <AddIcon />
                </FloatingActionButton>
              </>
            }
          />

          <Switch>
            <PrivateRoute
              exact
              path="/knowledge/article/new"
              component={() => (
                <Categories
                  groups={groupIds}
                  locations={locationIds}
                  onEditCategory={handleEditCategory}
                  onDeleteCategory={handleDeleteCategory}
                />
              )}
            />
            <PrivateRoute
              exact
              path="/knowledge"
              component={() => (
                <Categories
                  groups={groupIds}
                  locations={locationIds}
                  onEditCategory={handleEditCategory}
                  onDeleteCategory={handleDeleteCategory}
                />
              )}
            />
            <PrivateRoute
              path="/knowledge/training/:id"
              component={() => (
                <Categories
                  groups={groupIds}
                  locations={locationIds}
                  onEditCategory={handleEditCategory}
                  onDeleteCategory={handleDeleteCategory}
                />
              )}
            />
            <PrivateRoute
              exact
              path="/knowledge/category/:id/:action"
              component={() => (
                <Category
                  groups={groupIds}
                  locations={locationIds}
                  onEditArticle={handleEditArticle}
                  onDeleteArticle={handleDeleteArticle}
                  onEditCategory={handleEditCategory}
                  onDeleteCategory={handleDeleteCategory}
                />
              )}
            />
            <PrivateRoute
              exact
              path="/knowledge/category/:id"
              component={() => (
                <Category
                  groups={groupIds}
                  locations={locationIds}
                  onEditArticle={handleEditArticle}
                  onDeleteArticle={handleDeleteArticle}
                  onEditCategory={handleEditCategory}
                  onDeleteCategory={handleDeleteCategory}
                />
              )}
            />
            <PrivateRoute
              exact
              path="/knowledge/article/:id/:action"
              component={() => <Article onEditArticle={handleEditArticle} onDeleteArticle={handleDeleteArticle} />}
            />
            <PrivateRoute
              exact
              path="/knowledge/article/:id"
              component={() => <Article onEditArticle={handleEditArticle} onDeleteArticle={handleDeleteArticle} />}
            />
          </Switch>
        </Box>
      )}
    </>
  )
}

export default Knowledge
